import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, ChevronRight, ChevronLeft } from "lucide-react";
import api from "@/lib/api";
import { PostComposer } from "@/components/PostComposer";
import { PostCard } from "@/components/PostCard";
import { CATEGORIES } from "@/lib/categories";
import { timeAgo } from "@/lib/format";

export default function Home() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/posts");
      setPosts(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return posts;
    return posts.filter((p) => p.category === filter);
  }, [posts, filter]);

  const pinnedAnnouncement = useMemo(() =>
    posts.filter((p) => p.category === "announcements").slice(0, 1)[0],
    [posts]
  );

  const upsert = (updated) =>
    setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));

  const remove = (id) => setPosts((prev) => prev.filter((p) => p.id !== id));

  const addPost = (newPost) => setPosts((prev) => [newPost, ...prev]);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Feed</h1>
          <p className="text-sm text-muted-foreground mt-1">Latest from your CCNEO community.</p>
        </div>
      </div>

      {/* Pinned announcement banner */}
      {pinnedAnnouncement && (
        <Link
          to="/announcements"
          className="block bg-gradient-to-br from-[#B31B1B] to-[#7a1212] text-white rounded-2xl p-5 cornell-shadow-hover"
          data-testid="pinned-announcement-banner"
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 grid place-items-center shrink-0">
              <Megaphone className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-widest font-semibold opacity-90">Club Announcement</span>
                <span className="text-[10px] opacity-70">· {timeAgo(pinnedAnnouncement.created_at)}</span>
              </div>
              <p className="mt-1 text-[15px] leading-relaxed line-clamp-3">{pinnedAnnouncement.content}</p>
              <div className="mt-2 text-xs font-semibold underline underline-offset-2 opacity-90">View all announcements →</div>
            </div>
          </div>
        </Link>
      )}

      <PostComposer onPosted={addPost} />

      {/* Category filter */}
      <CategoryFilterRow filter={filter} setFilter={setFilter} />

      {loading ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="feed-loading">Loading feed…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground bg-white rounded-2xl border border-border" data-testid="feed-empty">
          No posts in this category yet. Be the first!
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((p) => (
            <PostCard key={p.id} post={p} onChange={upsert} onDelete={remove} />
          ))}
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`whitespace-nowrap px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors ${
        active
          ? "bg-[#B31B1B] text-white border-[#B31B1B]"
          : "bg-white text-neutral-700 border-border hover:bg-neutral-50"
      }`}
    >
      {children}
    </button>
  );
}

function CategoryFilterRow({ filter, setFilter }) {
  const scrollerRef = React.useRef(null);
  const [showRight, setShowRight] = React.useState(true);
  const [showLeft, setShowLeft] = React.useState(false);
  const [nudging, setNudging] = React.useState(false);

  const update = React.useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const remaining = el.scrollWidth - el.clientWidth - el.scrollLeft;
    setShowRight(remaining > 8);
    setShowLeft(el.scrollLeft > 8);
  }, []);

  // Recompute on scroll & resize
  React.useEffect(() => {
    update();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      el.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [update]);

  // First-load nudge: scroll right ~48px then back to 0 to reveal there's more
  React.useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    if (el.scrollWidth - el.clientWidth <= 8) return; // nothing to hint
    setNudging(true);
    const t1 = setTimeout(() => {
      el.scrollTo({ left: 56, behavior: "smooth" });
    }, 350);
    const t2 = setTimeout(() => {
      el.scrollTo({ left: 0, behavior: "smooth" });
    }, 1100);
    const t3 = setTimeout(() => setNudging(false), 1800);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const step = () => {
    const el = scrollerRef.current;
    if (!el) return 160;
    return Math.max(160, el.clientWidth * 0.6);
  };
  const scrollForward = () => scrollerRef.current?.scrollBy({ left: step(), behavior: "smooth" });
  const scrollBack = () => scrollerRef.current?.scrollBy({ left: -step(), behavior: "smooth" });

  return (
    <div className="relative -mx-4" data-testid="category-filter-row-wrapper">
      <div
        ref={scrollerRef}
        className="flex gap-2 overflow-x-auto no-scrollbar pb-1 scroll-smooth"
        style={{
          WebkitOverflowScrolling: "touch",
          paddingLeft: showLeft ? 56 : 16,
          paddingRight: showRight ? 56 : 16,
        }}
        data-testid="category-filter-row"
      >
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">All</FilterChip>
        {CATEGORIES.map((c) => (
          <FilterChip
            key={c.key}
            active={filter === c.key}
            onClick={() => setFilter(c.key)}
            testid={`filter-${c.key}`}
          >
            <span className="mr-1">{c.icon}</span>{c.label}
          </FilterChip>
        ))}
      </div>

      {/* Left-edge fade + arrow */}
      {showLeft && (
        <>
          <div
            className="pointer-events-none absolute top-0 left-0 h-full w-16 bg-gradient-to-r from-background via-background/85 to-transparent"
            data-testid="category-filter-fade-left"
            aria-hidden
          />
          <button
            type="button"
            onClick={scrollBack}
            aria-label="Scroll to see previous categories"
            data-testid="category-filter-arrow-left"
            className="absolute top-1/2 left-1.5 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-white text-[#B31B1B] border border-[#B31B1B]/30 shadow-md flex items-center justify-center hover:bg-[#B31B1B] hover:text-white transition-colors"
          >
            <ChevronLeft className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </>
      )}

      {/* Right-edge fade + arrow */}
      {showRight && (
        <>
          <div
            className="pointer-events-none absolute top-0 right-0 h-full w-16 bg-gradient-to-l from-background via-background/85 to-transparent"
            data-testid="category-filter-fade"
            aria-hidden
          />
          <button
            type="button"
            onClick={scrollForward}
            aria-label="Scroll to see more categories"
            data-testid="category-filter-arrow"
            className={`absolute top-1/2 right-1.5 -translate-y-1/2 z-10 h-8 w-8 rounded-full bg-white text-[#B31B1B] border border-[#B31B1B]/30 shadow-md flex items-center justify-center hover:bg-[#B31B1B] hover:text-white transition-colors ${
              nudging ? "animate-bounce-x" : ""
            }`}
          >
            <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </>
      )}
    </div>
  );
}
